import { useState, useEffect, useCallback } from 'react';
import { Modal } from 'react-bootstrap';
import Select from 'react-select';
import PropTypes from 'prop-types';
import CircularProgress from '@mui/material/CircularProgress';
import { API_REQUEST } from '../../helpers/request';
import { logger } from '../../helpers/logger';
import { PACKAGE_OPTIONS } from '../../constants/trades';
import { normalizeName } from '../../helpers/stringUtils';
import ErrorDisplay from '../error/ErrorDisplay';
import { parseError, handleApiError } from '../../helpers/errorHandler';
import AdditionalDocumentsSection from './AdditionalDocumentsSection';
import PricingBasisSection from './PricingBasisSection';
import SubcontractorQuoteCard from './SubcontractorQuoteCard';
import {
  MAX_FILES,
  MAX_SUBCONTRACTORS,
  FILE_SIZE_LIMITS,
} from '../../constants';
import {
  formatFileSize,
  calculateTotalSize,
  createFileObject,
} from '../../helpers/fileValidationUtils';
import useFileValidation from '../../hooks/useFileValidation';
import useRejectedFileTimeout from '../../hooks/useRejectedFileTimeout';
import './upload-modal.css';

const makeSubcontractor = () => ({
  id: crypto.randomUUID(),
  name: '',
  files: [],
});

const CrossIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);

const PlusIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <path d="M12 5v14M5 12h14" />
  </svg>
);

const DoneCheck = () => (
  <span className="um-done-check" aria-label="Section complete">
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M4 12l5 5L20 7" />
    </svg>
  </span>
);

const CheckIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M4 12l5 5L20 7" />
  </svg>
);

const InfoIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M12 8v4" />
    <path d="M12 16h.01" />
  </svg>
);

const BuildingIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M4 21V5a1 1 0 0 1 1-1h9a1 1 0 0 1 1 1v16" />
    <path d="M15 9h4a1 1 0 0 1 1 1v11" />
    <path d="M4 21h17" />
    <path d="M8 8h3M8 12h3M8 16h3" />
  </svg>
);

const createFullProjectBatch = async (
  projectData,
  packageName,
  files,
  boqFile,
  scopeText,
  callback
) => {
  try {
    // Create FormData for atomic batch upload
    const formData = new FormData();

    // Add JSON metadata with project info, package info and files
    const jsonData = {
      package_name: packageName,
      files: files.map((file) => ({
        file: {
          path: file.rawFile.name,
        },
        subcontractor: (file.subcontractor || '').trim(),
      })),
      // Optional BoQ / pricing document, stored at package level
      ...(boqFile && {
        boq_file: { file: { path: boqFile.rawFile.name } },
      }),
      // Add scope text if present
      ...(scopeText &&
        scopeText.trim() && {
          scope_of_works_text: scopeText.trim(),
        }),
      ...projectData, // Spread project data (either project_id or project_name + market_code)
    };
    formData.append('json', JSON.stringify(jsonData));

    // Add files with their original names as keys
    files.forEach((file) => {
      formData.append(file.rawFile.name, file.rawFile);
    });
    if (boqFile) {
      formData.append(boqFile.rawFile.name, boqFile.rawFile);
    }

    // Upload project and package atomically
    const response = await API_REQUEST.post('/batch/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    logger().log('Project and package created atomically', 'info');
    const projectId = response.data.project.id;
    const packageId = response.data.package.id;
    callback(projectId, packageId, response.data);
    return response.data;
  } catch (error) {
    logger().log(error, 'error');
    if (error.response?.status === 409) {
      throw {
        ...error,
        packageError: error.response.data.message || 'Package already exists',
      };
    }
    // Preserve the original axios error with response data
    throw error;
  }
};

const UploadModal = ({
  show,
  setShow,
  existingProjectId,
  existingPackages,
}) => {
  const [submittingForm, setFormSubmitting] = useState(false);
  const [isLoadingProject, setIsLoadingProject] = useState(false);
  const [projectName, setProjectName] = useState('');
  const [selectedPackages, setSelectedPackages] = useState([]);
  const [packageInput, setPackageInput] = useState('');
  const [boqFile, setBoqFile] = useState(null);
  const [boqDropError, setBoqDropError] = useRejectedFileTimeout(4000);
  const [marketOptionsList, setMarketOptionsList] = useState([]);
  const [isLoadingMarkets, setIsLoadingMarkets] = useState(true);
  const [selectedMarket, setSelectedMarket] = useState(null);
  const [subcontractors, setSubcontractors] = useState(() => [
    makeSubcontractor(),
    makeSubcontractor(),
  ]);
  const [lastAddedId, setLastAddedId] = useState(null);
  const [scopeText, setScopeText] = useState('');
  const [scopeTextError, setScopeTextError] = useState('');
  const [dropRejections, setDropRejections] = useRejectedFileTimeout(4000);
  const { validateFile, hasDuplicateFileName } = useFileValidation();
  const scopeEnabled = process.env.REACT_APP_SCOPE_OF_WORKS_ENABLED === 'true';
  const [errors, setErrors] = useState({
    packages: '',
    market: '',
    general: null,
  });

  const isExistingProject = !!existingProjectId;

  // Derived state over the grouped subcontractor model
  const allFiles = subcontractors.flatMap((sub) => sub.files);
  const totalFileSize = calculateTotalSize(allFiles);
  const exceedsTotalSize = totalFileSize > FILE_SIZE_LIMITS.MAX_TOTAL;
  const fileErrors = allFiles.filter((file) => file.errors.length > 0);
  const completeSubs = subcontractors.filter(
    (sub) => sub.name.trim() !== '' && sub.files.length > 0
  );
  const uniqueCompleteNames = new Set(
    completeSubs.map((sub) => normalizeName(sub.name))
  );
  const namelessWithFiles = subcontractors.filter(
    (sub) => sub.name.trim() === '' && sub.files.length > 0
  );
  const namedWithoutFiles = subcontractors.filter(
    (sub) => sub.name.trim() !== '' && sub.files.length === 0
  );
  const namedSubs = subcontractors.filter((sub) => sub.name.trim() !== '');
  const hasDuplicateSubNames =
    new Set(namedSubs.map((sub) => normalizeName(sub.name))).size <
    namedSubs.length;

  // Per-card state for the status chips and accents
  const subcontractorStatus = (sub) => {
    const hasName = sub.name.trim() !== '';
    const hasFiles = sub.files.length > 0;
    if (!hasName && !hasFiles) return 'empty';
    if (!hasName) return 'needs-name';
    const isDuplicate = subcontractors.some(
      (other) =>
        other.id !== sub.id &&
        normalizeName(other.name) === normalizeName(sub.name)
    );
    if (isDuplicate) return 'duplicate-name';
    if (!hasFiles) return 'needs-files';
    if (sub.files.some((file) => file.errors.length > 0)) return 'file-errors';
    return 'ready';
  };

  // Section-level completeness for the ✓ marks in section heads
  const projectDetailsComplete = isExistingProject
    ? selectedPackages.length > 0
    : projectName.trim() !== '' &&
      !!selectedMarket &&
      selectedPackages.length > 0;
  const subcontractorsComplete =
    uniqueCompleteNames.size >= 2 &&
    namelessWithFiles.length === 0 &&
    namedWithoutFiles.length === 0 &&
    !hasDuplicateSubNames &&
    fileErrors.length === 0 &&
    !exceedsTotalSize;

  // Build the list of outstanding requirements, most important first.
  const missingRequirements = [];
  if (!isExistingProject && projectName.trim() === '') {
    missingRequirements.push('name your project');
  }
  // Existing projects never send market_code, so a failed markets fetch
  // must not dead-end a flow that renders no market control
  if (!isExistingProject && !selectedMarket) {
    missingRequirements.push('select a market');
  }
  if (selectedPackages.length === 0) {
    missingRequirements.push('choose at least one trade for the work package');
  }
  if (boqFile && boqFile.errors.length > 0) {
    missingRequirements.push('fix the pricing document issue shown below');
  }
  if (uniqueCompleteNames.size === 0) {
    missingRequirements.push('add a quote for at least two subcontractors');
  } else if (uniqueCompleteNames.size === 1) {
    missingRequirements.push('add a quote for at least one more subcontractor');
  }
  if (namelessWithFiles.length > 0) {
    missingRequirements.push('name every subcontractor that has files');
  }
  if (hasDuplicateSubNames) {
    missingRequirements.push('give each subcontractor a unique name');
  }
  if (uniqueCompleteNames.size >= 2 && namedWithoutFiles.length > 0) {
    missingRequirements.push(
      `add quote files for ${namedWithoutFiles[0].name.trim()} or remove its card`
    );
  }
  if (fileErrors.length > 0) {
    missingRequirements.push('fix the file issues shown below');
  }
  if (exceedsTotalSize) {
    missingRequirements.push(
      `keep the total file size under ${formatFileSize(FILE_SIZE_LIMITS.MAX_TOTAL)}`
    );
  }
  if (scopeTextError) {
    missingRequirements.push('fix the scope of works text');
  }

  const isReady = missingRequirements.length === 0;
  const isSubmitDisabled = !isReady || submittingForm;

  const todoMessage =
    missingRequirements.length > 0
      ? missingRequirements
          .slice(0, 2)
          .join(' and ')
          .replace(/^./, (c) => c.toUpperCase())
      : '';

  const primaryLabel = isExistingProject
    ? 'Add package & analyse'
    : 'Create & analyse';

  // useEffect to fetch market options
  useEffect(() => {
    const fetchMarketOptions = async () => {
      setIsLoadingMarkets(true);
      try {
        const response = await API_REQUEST.get('/config/markets');
        setMarketOptionsList(response.data.markets || []);
        // Set default selected market to UK if available, after options are loaded
        const ukMarket = response.data.markets.find(
          (opt) => opt.value === 'UK'
        );
        if (ukMarket) {
          setSelectedMarket(ukMarket);
        } else if (response.data.markets.length > 0) {
          setSelectedMarket(response.data.markets[0]); // Fallback to the first market
        }
      } catch (error) {
        logger().log('Failed to fetch market options:', error, 'error');
        setErrors((prev) => ({
          ...prev,
          market: 'Failed to load market options.',
        }));
        setMarketOptionsList([]); // Ensure it's an array in case of error
      } finally {
        setIsLoadingMarkets(false);
      }
    };

    fetchMarketOptions();
  }, []);

  useEffect(() => {
    setErrors((prev) => ({
      ...prev,
      general: null,
    }));
  }, [subcontractors]);

  useEffect(() => {
    setErrors((prev) => ({
      ...prev,
      packages: '',
    }));
  }, [selectedPackages]);

  useEffect(() => {
    // Clear package error when project name changes (for new projects)
    if (!existingProjectId) {
      setErrors((prev) => ({
        ...prev,
        packages: '',
      }));
    }
  }, [projectName, existingProjectId]);

  const getDefaultMarket = useCallback((marketOptions) => {
    if (
      !marketOptions ||
      !Array.isArray(marketOptions) ||
      marketOptions.length === 0
    ) {
      return null;
    }
    return marketOptions.find((opt) => opt.value === 'UK') || marketOptions[0];
  }, []);

  const resetForm = ({ clearFormData = true } = {}) => {
    setErrors({
      packages: '',
      market: '',
      general: null,
    });

    // Only clear form data if explicitly requested
    if (clearFormData) {
      setSelectedPackages([]);
      setBoqFile(null);
      // Reset market to UK if available in the fetched list, otherwise first or null
      const defaultMarket = getDefaultMarket(marketOptionsList);
      setSelectedMarket(defaultMarket);
      setSubcontractors([makeSubcontractor(), makeSubcontractor()]);
      setScopeText('');
      setScopeTextError('');
      if (!existingProjectId) {
        setProjectName('');
      }
    }
    setFormSubmitting(false);
  };

  const handleClose = () => {
    // Only clear errors, preserve form data for better UX
    resetForm({ clearFormData: false });
    setShow(false);
  };

  const handleSuccessfulSubmission = () => {
    // Clear all form data after successful submission
    resetForm({ clearFormData: true });
  };

  // Scope text change handler
  const handleScopeTextChange = (newText, error) => {
    setScopeText(newText);
    setScopeTextError(error || '');
  };

  // Fetch project name when existingProjectId is provided
  useEffect(() => {
    const fetchProjectName = async () => {
      // Wait for the markets fetch to settle, but don't require it to have
      // returned options — the header and context strip need the project
      // name even when /config/markets fails, and the market mapping below
      // already falls back to the raw market_code
      if (existingProjectId && !isLoadingMarkets) {
        setIsLoadingProject(true);
        try {
          const response = await API_REQUEST.get(
            `/projects/${existingProjectId}`
          );
          setProjectName(response.data.name);
          const projectMarket = marketOptionsList.find(
            (opt) => opt.value === response.data.market_code
          );
          if (projectMarket) {
            setSelectedMarket(projectMarket);
          } else if (response.data.market_code) {
            // If market_code from project is not in our fetched list, log warning.
            // UX decision: either show it as a custom value or clear selection.
            // For now, setting it directly, assuming it might be a valid but unlisted code.
            logger().log(
              `Project's market code "${response.data.market_code}" not found in fetched market options. Displaying directly.`,
              'warn'
            );
            setSelectedMarket({
              value: response.data.market_code,
              label: response.data.market_code, // Fallback label
            });
          }
        } catch (error) {
          logger().log(error, 'error');
          const parsedError = parseError(error);
          setErrors((prev) => ({
            ...prev,
            general: parsedError,
          }));
        } finally {
          setIsLoadingProject(false);
        }
      } else {
        setProjectName('');
      }
    };

    fetchProjectName();
  }, [existingProjectId, isLoadingMarkets, marketOptionsList]);

  // ---------- subcontractor group handlers ----------

  const handleSubNameChange = (subId, value) => {
    setSubcontractors((prev) =>
      prev.map((sub) => (sub.id === subId ? { ...sub, name: value } : sub))
    );
  };

  const handleAddSubcontractor = () => {
    if (subcontractors.length >= MAX_SUBCONTRACTORS) {
      return;
    }
    const newSub = makeSubcontractor();
    setSubcontractors((prev) => [...prev, newSub]);
    setLastAddedId(newSub.id);
  };

  const handleRemoveSubcontractor = (subId) => {
    setSubcontractors((prev) => {
      const next = prev.filter((sub) => sub.id !== subId);
      // Two cards are always shown because two subcontractors are required
      while (next.length < 2) {
        next.push(makeSubcontractor());
      }
      return next;
    });
  };

  const handleFileRemove = (subId, fileId) => {
    setSubcontractors((prev) =>
      prev.map((sub) =>
        sub.id === subId
          ? {
              ...sub,
              files: sub.files.filter(
                (file) => String(file.id) !== String(fileId)
              ),
            }
          : sub
      )
    );
  };

  const handleDropAccepted = (subId, acceptedFiles) => {
    const remaining = MAX_FILES - allFiles.length;
    if (remaining <= 0) {
      setDropRejections([
        {
          subId,
          message: `Maximum of ${MAX_FILES} files reached — remove a file first`,
        },
      ]);
      return;
    }

    // Every multipart part is keyed by filename, so names must be unique
    // across quote files AND the BoQ document
    const takenFiles = boqFile ? [...allFiles, boqFile] : allFiles;
    const duplicate = acceptedFiles.find((file) =>
      hasDuplicateFileName(file.name, takenFiles)
    );
    if (duplicate) {
      setDropRejections([
        {
          subId,
          message: `A file named "${duplicate.name}" has already been added`,
        },
      ]);
      return;
    }

    const toAdd = acceptedFiles.slice(0, remaining).map((file) =>
      createFileObject(file, false, {
        errors: validateFile(file),
      })
    );

    setSubcontractors((prev) =>
      prev.map((sub) =>
        sub.id === subId ? { ...sub, files: [...sub.files, ...toAdd] } : sub
      )
    );

    const skipped = acceptedFiles.length - toAdd.length;
    if (skipped > 0) {
      setDropRejections([
        {
          subId,
          message: `Only ${MAX_FILES} files can be added in total — ${skipped} ${skipped === 1 ? 'file was' : 'files were'} skipped`,
        },
      ]);
    }
  };

  const handleDropRejected = (subId, fileRejections) => {
    const first = Array.isArray(fileRejections) ? fileRejections[0] : null;
    const reason =
      first?.errors?.[0]?.message ||
      'File type not supported. Use PDF, Excel, CSV, Word or TXT files.';
    setDropRejections([
      { subId, message: `${first?.file?.name || 'File'}: ${reason}` },
    ]);
  };

  // ---------- pricing basis (BoQ) handlers ----------

  const handleBoqDropAccepted = (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) {
      return;
    }
    if (hasDuplicateFileName(file.name, allFiles)) {
      setBoqDropError([
        `A quote file named "${file.name}" has already been added`,
      ]);
      return;
    }
    setBoqFile(createFileObject(file, false, { errors: validateFile(file) }));
  };

  const handleBoqDropRejected = (fileRejections) => {
    const first = Array.isArray(fileRejections) ? fileRejections[0] : null;
    const reason =
      first?.errors?.[0]?.message ||
      'File type not supported. Use XLSX, XLS, CSV or PDF.';
    setBoqDropError([`${first?.file?.name || 'File'}: ${reason}`]);
  };

  const handleSubmit = async () => {
    // Don't proceed if submission requirements aren't met
    if (isSubmitDisabled) {
      return;
    }

    setFormSubmitting(true);

    try {
      // A package is often composed of several trades (e.g. Aluminium Doors +
      // Aluminium Windows) — the joined labels form the package name
      const packageName = selectedPackages.map((pkg) => pkg.label).join(', ');

      const handleTenderAnalysis = (projectId, packageId) => {
        handleSuccessfulSubmission();

        // Redirect directly to result page
        window.location.href = `/dashboard/project/${projectId}/package/${packageId}/result`;
      };

      // Prepare project data based on whether it's existing or new project
      const projectData = existingProjectId
        ? {
            project_id: existingProjectId,
          }
        : {
            project_name: projectName,
            market_code: selectedMarket.value,
          };

      // Flatten the grouped subcontractor state into the existing API format
      const filesPayload = subcontractors.flatMap((sub) =>
        sub.files.map((file) => ({ ...file, subcontractor: sub.name }))
      );

      // Create project and package atomically using the batch endpoint
      await createFullProjectBatch(
        projectData,
        packageName,
        filesPayload,
        boqFile,
        scopeText,
        handleTenderAnalysis
      );
    } catch (error) {
      setFormSubmitting(false);

      // Handle package-specific errors (409 conflict)
      if (error.packageError) {
        setErrors((prev) => ({
          ...prev,
          packages: error.packageError,
        }));
      } else {
        // Use the new error handler for all other errors
        const parsedError = handleApiError(error, {
          context: {
            action: 'upload_quotes',
            projectId: existingProjectId,
            packageName: selectedPackages.map((pkg) => pkg.label).join(', '),
            fileCount: allFiles.length,
          },
          defaultMessage: 'Failed to process request. Please try again.',
        });

        setErrors((prev) => ({
          ...prev,
          general: parsedError,
        }));
      }
      // Form data is preserved - user doesn't lose their input on errors
    }
  };

  const filteredPackages = existingPackages
    ? PACKAGE_OPTIONS.filter((option) => {
        return !existingPackages.some((pkg) => {
          // Split package name into individual labels
          const existingLabels = pkg.name
            .split(',')
            .map((label) => label.trim());
          // Check if the current option label matches any of the existing labels
          return existingLabels.includes(option.label);
        });
      })
    : PACKAGE_OPTIONS;

  // At the file cap the dropzones stay active so handleDropAccepted can
  // explain the limit; only a blown total size hard-disables them
  const dropDisabled = exceedsTotalSize;
  const existingPackageNames = Array.isArray(existingPackages)
    ? existingPackages.map((pkg) => pkg.name)
    : [];

  const isLoading = isLoadingProject || isLoadingMarkets;

  const packageSelect = (
    <div className="um-field">
      <span className="um-label" id="um-package-label">
        Work package <span className="req">*</span>
      </span>
      <Select
        isMulti
        options={filteredPackages}
        value={selectedPackages}
        onChange={(newPackages) => setSelectedPackages(newPackages || [])}
        placeholder="Search trades…"
        className="um-package-select"
        classNamePrefix="um-rs"
        aria-labelledby="um-package-label"
        inputValue={packageInput}
        onInputChange={setPackageInput}
        menuIsOpen={packageInput.length > 0}
        components={{ DropdownIndicator: null }}
      />
      <p className="um-field-hint">
        A package can combine several trades — each one you pick becomes part of
        the package name.
      </p>
      {errors.packages && <p className="um-field-error">{errors.packages}</p>}
    </div>
  );

  const pricingBasisSection = (
    <PricingBasisSection
      boqFile={boqFile}
      onDropAccepted={handleBoqDropAccepted}
      onDropRejected={handleBoqDropRejected}
      onRemove={() => setBoqFile(null)}
      dropError={boqDropError[0]}
    />
  );

  return (
    <Modal
      show={show}
      onHide={handleClose}
      className="um-modal-root"
      dialogClassName="um-dialog"
      contentClassName="um-content"
      aria-labelledby="um-title"
    >
      {isLoading ? (
        <div className="um-loading">
          <CircularProgress />
        </div>
      ) : (
        <>
          <div className="um-header">
            <div>
              <h1 className="um-title" id="um-title">
                {isExistingProject
                  ? `Add package to ${projectName}`
                  : 'Create project'}
              </h1>
              <p className="um-sub">
                {isExistingProject
                  ? 'Add pricing documents for a new work package.'
                  : 'Set up a work package and add documents for analysis.'}
              </p>
            </div>
            <button
              type="button"
              className="um-close"
              aria-label="Close"
              onClick={handleClose}
            >
              <CrossIcon />
            </button>
          </div>

          <div className="um-body">
            {isExistingProject ? (
              <>
                <div className="um-card um-context-strip">
                  <span className="um-context-icon">
                    <BuildingIcon />
                  </span>
                  <div className="um-context-main">
                    <div className="um-context-name">{projectName}</div>
                    <div className="um-context-meta">
                      {existingPackageNames.length > 0
                        ? `${existingPackageNames.length} existing ${existingPackageNames.length === 1 ? 'package' : 'packages'} · ${existingPackageNames.join(', ')}`
                        : 'No existing packages'}
                    </div>
                  </div>
                  {selectedMarket && (
                    <span className="um-chip tone-neutral">
                      {selectedMarket.label}
                    </span>
                  )}
                </div>

                <div className="um-card">
                  <h2 className="um-card-title">
                    Package details
                    {projectDetailsComplete && <DoneCheck />}
                  </h2>
                  {packageSelect}
                </div>

                {pricingBasisSection}
              </>
            ) : (
              <div className="um-card">
                <h2 className="um-card-title">
                  Project details
                  {projectDetailsComplete && <DoneCheck />}
                </h2>
                <div className="um-grid2">
                  <div className="um-field">
                    <label className="um-label" htmlFor="um-project-name">
                      Project name <span className="req">*</span>
                    </label>
                    <input
                      id="um-project-name"
                      type="text"
                      className="um-input"
                      placeholder="e.g. Westbridge House"
                      value={projectName}
                      onChange={(e) => setProjectName(e.target.value)}
                    />
                  </div>
                  <div className="um-field">
                    <span className="um-label" id="um-market-label">
                      Market <span className="req">*</span>
                    </span>
                    <Select
                      options={marketOptionsList}
                      value={selectedMarket}
                      onChange={setSelectedMarket}
                      placeholder="Select market…"
                      className="um-market-select"
                      classNamePrefix="um-rs"
                      aria-labelledby="um-market-label"
                    />
                    {errors.market && (
                      <p className="um-field-error">{errors.market}</p>
                    )}
                  </div>
                </div>
                <div style={{ marginTop: 14 }}>{packageSelect}</div>
              </div>
            )}

            {!isExistingProject && pricingBasisSection}

            <div>
              <div className="um-sec-head">
                <div className="um-sec-head-main">
                  <h2 className="um-sec-title">
                    Subcontractor quotes
                    {subcontractorsComplete && <DoneCheck />}
                  </h2>
                  <p className="um-sec-sub">
                    Add at least two subcontractors and drop each one&apos;s
                    quote files into their card.
                  </p>
                </div>
                {allFiles.length > 0 && (
                  <span
                    className={`um-sec-meta ${exceedsTotalSize ? 'over-limit' : ''}`}
                  >
                    {allFiles.length} {allFiles.length === 1 ? 'file' : 'files'}{' '}
                    · {formatFileSize(totalFileSize)} of{' '}
                    {formatFileSize(FILE_SIZE_LIMITS.MAX_TOTAL)}
                  </span>
                )}
              </div>
              <div className="um-sub-stack">
                {subcontractors.map((sub, index) => (
                  <SubcontractorQuoteCard
                    key={sub.id}
                    subcontractor={sub}
                    index={index}
                    onNameChange={handleSubNameChange}
                    onRemove={handleRemoveSubcontractor}
                    onFileRemove={handleFileRemove}
                    onDropAccepted={handleDropAccepted}
                    onDropRejected={handleDropRejected}
                    dropDisabled={dropDisabled}
                    dropError={
                      dropRejections.find((r) => r.subId === sub.id)?.message
                    }
                    autoFocusName={sub.id === lastAddedId}
                    status={subcontractorStatus(sub)}
                  />
                ))}
                <button
                  type="button"
                  className="um-add-sub"
                  onClick={handleAddSubcontractor}
                  disabled={subcontractors.length >= MAX_SUBCONTRACTORS}
                >
                  <PlusIcon />
                  Add subcontractor
                  <span className="hint">up to {MAX_SUBCONTRACTORS}</span>
                </button>
              </div>
            </div>

            {scopeEnabled && (
              <AdditionalDocumentsSection
                scopeText={scopeText}
                onScopeTextChange={handleScopeTextChange}
                error={scopeTextError}
              />
            )}

            {errors.general && (
              <ErrorDisplay
                error={errors.general}
                showContactSupport={false}
                showSuggestions={true}
                showErrorCode={false}
                variant="standard"
              />
            )}
          </div>

          <div className="um-footer">
            {isReady ? (
              <div className="um-ready-list">
                <span className="um-ready-item">
                  <CheckIcon />
                  Project details
                </span>
                <span className="um-ready-item">
                  <CheckIcon />
                  {uniqueCompleteNames.size} subcontractors
                </span>
                <span className="um-ready-item">
                  <CheckIcon />
                  {allFiles.length} quote{' '}
                  {allFiles.length === 1 ? 'file' : 'files'}
                </span>
              </div>
            ) : (
              <div className="um-todo-note">
                <InfoIcon />
                {todoMessage}
              </div>
            )}
            <div className="um-footer-btns">
              <button
                type="button"
                className="um-btn um-btn-secondary"
                onClick={handleClose}
                disabled={submittingForm}
              >
                Cancel
              </button>
              <button
                type="button"
                className="um-btn um-btn-primary"
                onClick={handleSubmit}
                disabled={isSubmitDisabled}
              >
                {submittingForm && (
                  <CircularProgress size={14} color="inherit" />
                )}
                {primaryLabel}
              </button>
            </div>
          </div>
        </>
      )}
    </Modal>
  );
};

UploadModal.propTypes = {
  show: PropTypes.bool.isRequired,
  setShow: PropTypes.func.isRequired,
  existingProjectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  existingPackages: PropTypes.array,
};

export default UploadModal;
